import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { PlanProvider } from './contexts/PlanContext';
import { Toaster } from './components/ui/sonner';
import AppLayout from './components/AppLayout';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import DashboardPage from './pages/DashboardPage';
import ClientsPage from './pages/ClientsPage';
import ClientDetailPage from './pages/ClientDetailPage';
import ProjectsPage from './pages/ProjectsPage';
import ProjectDetailPage from './pages/ProjectDetailPage';
import InvoicesPage from './pages/InvoicesPage';
import ExpensesPage from './pages/ExpensesPage';
import CreditNotesPage from './pages/CreditNotesPage';
import ReportsPage from './pages/ReportsPage';
import AdminPage from './pages/AdminPage';
import SettingsPage from './pages/SettingsPage';
import SuperAdminPage from './pages/SuperAdminPage';
import AccountantHandoverPage from './pages/AccountantHandoverPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import LineItemTemplatesPage from './pages/LineItemTemplatesPage';
import PublicDocumentViewPage from './pages/PublicDocumentViewPage';
import ServiceNoteResponsePage from './pages/ServiceNoteResponsePage';
import HandoffCallback from './pages/HandoffCallback';
import BankingPage from './pages/BankingPage';
import HelpPage from './pages/HelpPage';
import SecurityStatementPage from './pages/SecurityStatementPage';
import ComplianceCenterPage from './pages/ComplianceCenterPage';
import { LocaleProvider } from './contexts/LocaleContext';
import './App.css';

function ProtectedRoute({ children, allowedRoles = null }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-slate-500">Loading...</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  
  // Check role if allowedRoles is specified
  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return (
      <AppLayout>
        <div className="p-8 text-center">
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Access Denied</h1>
          <p className="text-slate-500">You don't have permission to access this page.</p>
        </div>
      </AppLayout>
    );
  }
  
  return <AppLayout>{children}</AppLayout>;
}

function PublicRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to="/" replace />;
  return children;
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <LocaleProvider>
          <PlanProvider>
          <Routes>
            <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
            <Route path="/auth/handoff" element={<HandoffCallback />} />
            <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
            <Route path="/verify-email/:token" element={<VerifyEmailPage />} />
            <Route path="/view/:token" element={<PublicDocumentViewPage />} />
            <Route path="/service-note/:token" element={<ServiceNoteResponsePage />} />
            <Route path="/security" element={<SecurityStatementPage />} />
            <Route path="/legal" element={<ComplianceCenterPage />} />
            <Route path="/" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
            <Route path="/clients" element={<ProtectedRoute><ClientsPage /></ProtectedRoute>} />
            <Route path="/clients/:clientId" element={<ProtectedRoute><ClientDetailPage /></ProtectedRoute>} />
            <Route path="/projects" element={<ProtectedRoute><ProjectsPage /></ProtectedRoute>} />
            <Route path="/projects/:projectId" element={<ProtectedRoute><ProjectDetailPage /></ProtectedRoute>} />
            <Route path="/invoices" element={<ProtectedRoute><InvoicesPage /></ProtectedRoute>} />
            <Route path="/expenses" element={<ProtectedRoute><ExpensesPage /></ProtectedRoute>} />
            <Route path="/banking" element={<ProtectedRoute><BankingPage /></ProtectedRoute>} />
            <Route path="/help" element={<ProtectedRoute><HelpPage /></ProtectedRoute>} />
            <Route path="/credit-notes" element={<ProtectedRoute><CreditNotesPage /></ProtectedRoute>} />
            <Route path="/templates" element={<ProtectedRoute><LineItemTemplatesPage /></ProtectedRoute>} />
            <Route path="/reports" element={<ProtectedRoute><ReportsPage /></ProtectedRoute>} />
            <Route path="/reports/accountant-handover" element={<ProtectedRoute><AccountantHandoverPage /></ProtectedRoute>} />
            <Route path="/settings" element={<ProtectedRoute allowedRoles={['super_admin', 'admin', 'account_owner']}><SettingsPage /></ProtectedRoute>} />
            <Route path="/admin" element={<ProtectedRoute allowedRoles={['super_admin']}><AdminPage /></ProtectedRoute>} />
            <Route path="/super-admin" element={<ProtectedRoute allowedRoles={['super_admin']}><SuperAdminPage /></ProtectedRoute>} />
          </Routes>
          <Toaster position="top-right" richColors />
          </PlanProvider>
        </LocaleProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
